import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: ((this: SpeechRecognitionLike, ev: Event) => unknown) | null;
  onresult:
    | ((this: SpeechRecognitionLike, ev: SpeechRecognitionEventLike) => unknown)
    | null;
  onerror:
    | ((
        this: SpeechRecognitionLike,
        ev: SpeechRecognitionErrorEventLike,
      ) => unknown)
    | null;
  onend: ((this: SpeechRecognitionLike, ev: Event) => unknown) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionEventLike extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike extends Event {
  error:
    | 'aborted'
    | 'audio-capture'
    | 'bad-grammar'
    | 'language-not-supported'
    | 'network'
    | 'no-speech'
    | 'not-allowed'
    | 'service-not-allowed';
  message: string;
}

interface SpeechRecognitionResultListLike {
  length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}

export interface VoiceSearchConfig {
  lang?: string;
  interimResults?: boolean;
  maxAlternatives?: number;
}

export type VoiceSearchErrorCode =
  | 'unsupported'
  | 'permission-denied'
  | 'no-speech'
  | 'audio-capture'
  | 'network'
  | 'aborted'
  | 'unknown';

export interface VoiceSearchResult {
  transcript: string;
  isFinal: boolean;
}

export interface VoiceSearchError {
  code: VoiceSearchErrorCode;
  message: string;
}

interface SpeechWindow extends Window {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

@Injectable({
  providedIn: 'root',
})
export class VoiceSearchService {
  private recognition: SpeechRecognitionLike | null = null;
  private readonly listeningSubject = new BehaviorSubject<boolean>(false);
  private readonly resultSubject = new Subject<VoiceSearchResult>();
  private readonly errorSubject = new Subject<VoiceSearchError>();
  private readonly isBrowser: boolean;

  readonly listening$: Observable<boolean> =
    this.listeningSubject.asObservable();
  readonly results$: Observable<VoiceSearchResult> =
    this.resultSubject.asObservable();
  readonly errors$: Observable<VoiceSearchError> =
    this.errorSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private readonly ngZone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  isSupported(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    // Check for secure context (HTTPS or localhost)
    const isSecureContext = window.isSecureContext;
    if (!isSecureContext) {
      console.warn(
        '[VoiceSearch] Voice search requires HTTPS in production. Current origin:',
        window.location.origin,
      );
      return false;
    }

    const hasRecognition = this.getRecognitionCtor() !== null;
    if (!hasRecognition) {
      console.warn(
        '[VoiceSearch] SpeechRecognition API not available in this browser',
      );
    }

    return hasRecognition;
  }

  startListening(config: VoiceSearchConfig = {}): void {
    if (!this.isBrowser) {
      console.warn('[VoiceSearch] Not running in browser context');
      return;
    }

    // Check secure context first
    if (!window.isSecureContext) {
      const errorMsg =
        'Voice search requires HTTPS. Please ensure your site is served over HTTPS.';
      console.error('[VoiceSearch]', errorMsg);
      this.errorSubject.next({
        code: 'unsupported',
        message: errorMsg,
      });
      return;
    }

    if (!this.isSupported()) {
      const errorMsg = 'Voice search is not supported in this browser.';
      console.error('[VoiceSearch]', errorMsg);
      this.errorSubject.next({
        code: 'unsupported',
        message: errorMsg,
      });
      return;
    }

    if (this.listeningSubject.value) {
      this.stopListening();
    }

    const RecognitionCtor = this.getRecognitionCtor();
    if (!RecognitionCtor) {
      console.error('[VoiceSearch] Recognition constructor not available');
      return;
    }

    try {
      console.log('[VoiceSearch] Initializing speech recognition...');
      this.recognition = new RecognitionCtor();
      this.recognition.continuous = false;
      this.recognition.interimResults = config.interimResults ?? true;
      this.recognition.lang = config.lang ?? 'en-IN';
      this.recognition.maxAlternatives = config.maxAlternatives ?? 1;

      this.recognition.onstart = () => {
        console.log('[VoiceSearch] Listening started');
        this.ngZone.run(() => this.listeningSubject.next(true));
      };

      this.recognition.onresult = (event: SpeechRecognitionEventLike) => {
        this.ngZone.run(() => {
          let transcript = '';
          let hasFinal = false;

          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const result = event.results[i];
            const text = result?.[0]?.transcript ?? '';
            transcript += text;
            if (result?.isFinal) {
              hasFinal = true;
            }
          }

          const normalizedTranscript = transcript.trim();
          if (normalizedTranscript) {
            console.log('[VoiceSearch] Transcript:', normalizedTranscript);
            this.resultSubject.next({
              transcript: normalizedTranscript,
              isFinal: hasFinal,
            });
          }
        });
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
        console.error('[VoiceSearch] Error:', event.error, event.message);
        this.ngZone.run(() => {
          this.errorSubject.next(this.mapError(event.error));
        });
      };

      this.recognition.onend = () => {
        console.log('[VoiceSearch] Listening ended');
        this.ngZone.run(() => {
          this.listeningSubject.next(false);
          this.teardownRecognition();
        });
      };

      this.recognition.start();
      console.log('[VoiceSearch] Recognition started successfully');
    } catch (error) {
      console.error('[VoiceSearch] Failed to start recognition:', error);
      this.errorSubject.next({
        code: 'unknown',
        message: `Failed to initialize voice search: ${error}`,
      });
      this.teardownRecognition();
    }
  }

  stopListening(): void {
    if (!this.recognition) {
      this.listeningSubject.next(false);
      return;
    }

    this.recognition.stop();
  }

  destroy(): void {
    if (this.recognition) {
      this.recognition.abort();
    }
    this.teardownRecognition();
    this.listeningSubject.next(false);
  }

  private teardownRecognition(): void {
    if (!this.recognition) {
      return;
    }
    this.recognition.onstart = null;
    this.recognition.onresult = null;
    this.recognition.onerror = null;
    this.recognition.onend = null;
    this.recognition = null;
  }

  private getRecognitionCtor(): SpeechRecognitionCtor | null {
    if (!this.isBrowser) {
      return null;
    }
    const speechWindow = window as SpeechWindow;
    return (
      speechWindow.SpeechRecognition ??
      speechWindow.webkitSpeechRecognition ??
      null
    );
  }

  private mapError(
    error: SpeechRecognitionErrorEventLike['error'],
  ): VoiceSearchError {
    switch (error) {
      case 'not-allowed':
      case 'service-not-allowed':
        return {
          code: 'permission-denied',
          message:
            'Microphone permission was denied. Please allow microphone access.',
        };
      case 'no-speech':
        return {
          code: 'no-speech',
          message: 'No speech detected. Please try again.',
        };
      case 'audio-capture':
        return {
          code: 'audio-capture',
          message: 'No microphone was found. Please check your audio device.',
        };
      case 'network':
        return {
          code: 'network',
          message: 'Network error occurred during voice recognition.',
        };
      case 'aborted':
        return {
          code: 'aborted',
          message: 'Voice recognition was stopped.',
        };
      default:
        return {
          code: 'unknown',
          message: 'Unable to process voice input. Please try again.',
        };
    }
  }
}
